import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaArrowLeft, FaArrowRight, FaEnvelope, FaImages } from "react-icons/fa";
import logoForHeroSection1 from "../../assets/images/logoForHeroSection1.svg";
import { ROUTES } from "../../constants/routes";
import { scrollToTop } from "../../utils/scroll";
import { isRtlLanguage } from "../../utils/i18n";
import HeroSocialLinks from "./HeroSocialLinks";
import "./HeroSection.css";

const OVERLAY_CLASS = {
  default: "hero-color-overlay hero-color-overlay--default",
  deep: "hero-color-overlay hero-color-overlay--deep",
  contact: "hero-color-overlay hero-color-overlay--contact",
  bottom: "hero-color-overlay hero-color-overlay--bottom",
};

function HeroSection({
  backgroundImage,
  blendClass,
  overlayTone = "default",
  titleKey,
  subtitleKey,
  variant = "slide",
  subtitleSize = "lg",
}) {
  const { t, i18n } = useTranslation();
  const isRtl = isRtlLanguage(i18n.language);
  const isPage = variant === "page";
  const overlayClass =
    blendClass || OVERLAY_CLASS[overlayTone] || OVERLAY_CLASS.default;
  const ArrowIcon = isRtl ? FaArrowLeft : FaArrowRight;
  const rowDirection = isRtl ? "md:flex-row-reverse" : "md:flex-row";

  return (
    <section
      className={`hero-slide${isPage ? " hero-slide--page" : " hero-slide--home"}`}
      aria-label={isPage ? t(titleKey) : undefined}
    >
      <div
        className="hero-bg-image absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${backgroundImage})` }}
        aria-hidden="true"
      />
      <div className={`absolute inset-0 ${overlayClass}`} aria-hidden="true" />
      <div
        className={
          isPage
            ? "hero-page-gradient absolute inset-0"
            : "hero-gradient absolute inset-0"
        }
        aria-hidden="true"
      />
      <div className="hero-vignette absolute inset-0" aria-hidden="true" />

      <div
        className={`hero-inner relative z-10 mx-auto flex w-full max-w-6xl items-center px-4 sm:px-6 lg:max-w-7xl lg:px-8${
          isPage ? " hero-inner--page" : " hero-inner--home"
        }`}
        dir={isRtl ? "rtl" : "ltr"}
      >
        <div
          className={`hero-content-inner flex w-full flex-col items-center gap-5 ${rowDirection} md:items-stretch md:gap-6 lg:gap-8`}
        >
          {!isPage && (
            <div className="hero-logo-wrap md:w-[34%] lg:w-[32%]">
              <div className="hero-logo-ring">
                <img
                  src={logoForHeroSection1}
                  alt="CAF Sports"
                  className="hero-logo mx-auto w-[7.5rem] sm:w-[9rem] md:w-full md:max-w-[10rem] lg:max-w-[12rem]"
                  loading="eager"
                  decoding="async"
                />
              </div>
            </div>
          )}

          <div className={`flex flex-1 flex-col ${isPage ? "w-full" : ""}`}>
            <div
              className={
                isPage
                  ? "hero-text-panel hero-text-panel--page"
                  : "hero-text-panel hero-text-panel--slide text-center md:text-start"
              }
            >
              <span className={isPage ? "hero-page-eyebrow" : "hero-eyebrow"}>
                {t("CAF")} {t("SPORTS")}
              </span>
              <div
                className={
                  isPage
                    ? "hero-title-accent hero-title-accent--page"
                    : "hero-title-accent hero-title-accent--slide"
                }
                aria-hidden="true"
              />
              <h1 className={isPage ? "hero-copy hero-page-title" : "hero-copy hero-slide-title"}>
                {t(titleKey)}
              </h1>
              {subtitleKey ? (
                <p
                  className={
                    isPage
                      ? "hero-copy hero-page-subtitle mx-auto"
                      : `hero-copy hero-subtitle hero-subtitle--${subtitleSize} text-center md:text-start md:me-auto`
                  }
                >
                  {t(subtitleKey)}
                </p>
              ) : null}

              {!isPage && (
                <div className="hero-cta-row">
                  <Link
                    to={ROUTES.projects}
                    onClick={scrollToTop}
                    className="hero-btn hero-btn--primary"
                  >
                    <span
                      className="hero-btn__icon-wrap hero-btn__icon-wrap--lead"
                      aria-hidden="true"
                    >
                      <FaImages className="hero-btn__icon" />
                    </span>
                    <span className="hero-btn__label">{t("Our Projects")}</span>
                    <span className="hero-btn__icon-wrap" aria-hidden="true">
                      <ArrowIcon className="hero-btn__icon" />
                    </span>
                  </Link>
                  <Link
                    to={ROUTES.contact}
                    onClick={scrollToTop}
                    className="hero-btn hero-btn--secondary"
                  >
                    <span
                      className="hero-btn__icon-wrap hero-btn__icon-wrap--muted"
                      aria-hidden="true"
                    >
                      <FaEnvelope className="hero-btn__icon" />
                    </span>
                    <span className="hero-btn__label">{t("Contact Us")}</span>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {!isPage && <HeroSocialLinks isRtl={isRtl} />}
    </section>
  );
}

export default HeroSection;
